/**
 * Settings tab: Anruf-Weiterleitung.
 *
 * Three concerns share this view:
 *
 *  1. **Mode** — selects how `transfer_call` may be used at all.
 *  2. **Directory** — named numbers the LLM may dial. Each row needs
 *     full first+last name (we surface the schema's refine error
 *     inline). The fixed-fallback dropdown is enabled only when the
 *     mode includes `fixed`, and its options come from this list.
 *  3. **Trusted callers** — a CallerID whitelist that relaxes the
 *     strict full-name gate when one of these numbers calls in.
 *
 * The admin-locked default rules block lives in `LlmTab` alongside
 * the other security prompts (we don't duplicate it here).
 */
import * as React from 'react';
import { Plus, Trash2, ShieldCheck, Phone, UserRound } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../../components/ui/card';
import { Input } from '../../../../components/ui/input';
import { Label } from '../../../../components/ui/label';
import { Button } from '../../../../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../../components/ui/select';
import type { FormState } from '../state';
import type { TransferMode } from '@cp3/shared';

interface Props {
  form: FormState;
  setForm: React.Dispatch<React.SetStateAction<FormState>>;
}

const MODE_OPTIONS: Array<{ value: TransferMode; label: string; help: string }> = [
  {
    value: 'off',
    label: 'Aus — keine Weiterleitung',
    help: 'Die KI bietet keine Weiterleitung an. Tool ist deaktiviert.',
  },
  {
    value: 'directory',
    label: 'Verzeichnis (nur namentlich genannte Personen)',
    help:
      'Weiterleitung NUR, wenn der Anrufer den vollständigen Vor- UND Nachnamen einer hinterlegten Zielperson nennt.',
  },
  {
    value: 'fixed',
    label: 'Fixe Fallback-Nummer',
    help:
      'Die KI darf den Anruf NUR auf eine voreingestellte Nummer weiterleiten — z. B. Technik / Support — wenn der Anrufer mit einem Menschen sprechen möchte.',
  },
  {
    value: 'directory+fixed',
    label: 'Beides: Verzeichnis UND Fallback',
    help:
      'Namentliche Treffer aus dem Verzeichnis, plus die fixe Fallback-Nummer für „mit einem Mitarbeiter sprechen"-Anfragen ohne Namen.',
  },
];

export function TransferTab({ form, setForm }: Props) {
  const mode = (form.transferMode ?? 'off') as TransferMode;
  const targets = form.transferTargets ?? [];
  const trusted = form.trustedCallers ?? [];

  function setMode(next: TransferMode) {
    setForm((p) => ({
      ...p,
      transferMode: next,
      // Wenn der Modus keine Fallback-Nummer mehr braucht, leeren wir
      // den Pointer — sonst hängt eine inaktive Auswahl im State.
      transferFallbackTargetId:
        next === 'fixed' || next === 'directory+fixed'
          ? p.transferFallbackTargetId ?? null
          : null,
    }));
  }

  function addTarget() {
    setForm((p) => ({
      ...p,
      transferTargets: [
        ...(p.transferTargets ?? []),
        {
          id: cryptoRandomId(),
          displayName: '',
          phoneNumber: '',
          role: '',
        },
      ],
    }));
  }

  function updateTarget(id: string, patch: Partial<NonNullable<FormState['transferTargets']>[number]>) {
    setForm((p) => ({
      ...p,
      transferTargets: (p.transferTargets ?? []).map((t) =>
        t.id === id ? { ...t, ...patch } : t,
      ),
    }));
  }

  function deleteTarget(id: string) {
    setForm((p) => ({
      ...p,
      transferTargets: (p.transferTargets ?? []).filter((t) => t.id !== id),
      transferFallbackTargetId:
        p.transferFallbackTargetId === id ? null : p.transferFallbackTargetId,
    }));
  }

  function addTrusted() {
    setForm((p) => ({
      ...p,
      trustedCallers: [
        ...(p.trustedCallers ?? []),
        { id: cryptoRandomId(), phoneNumber: '', displayName: '' },
      ],
    }));
  }

  function updateTrusted(id: string, patch: Partial<NonNullable<FormState['trustedCallers']>[number]>) {
    setForm((p) => ({
      ...p,
      trustedCallers: (p.trustedCallers ?? []).map((t) =>
        t.id === id ? { ...t, ...patch } : t,
      ),
    }));
  }

  function deleteTrusted(id: string) {
    setForm((p) => ({
      ...p,
      trustedCallers: (p.trustedCallers ?? []).filter((t) => t.id !== id),
    }));
  }

  const fallbackEligible = mode === 'fixed' || mode === 'directory+fixed';
  const directoryEligible = mode === 'directory' || mode === 'directory+fixed';
  const namedTargets = targets.filter((t) => (t.displayName ?? '').trim().length > 0);

  return (
    <div className="flex flex-col gap-6">
      {/* ─── Mode ─────────────────────────────────────────────────── */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Phone className="h-5 w-5" /> Weiterleitungs-Modus
          </CardTitle>
          <CardDescription>
            Steuert, ob und wie der KI-Agent Anrufe an andere Nummern weiterleiten darf.
            Strenge Regeln (volle Vor- + Nachnamen-Pflicht) gelten zusätzlich automatisch
            via System-Prompt.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-3">
          <Select value={mode} onValueChange={(v) => setMode(v as TransferMode)}>
            <SelectTrigger className="w-full md:w-[420px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {MODE_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground">
            {MODE_OPTIONS.find((o) => o.value === mode)?.help}
          </p>

          {fallbackEligible && (
            <div className="mt-2 flex flex-col gap-2 border-t border-border pt-3">
              <Label>Fixe Fallback-Nummer</Label>
              <Select
                value={form.transferFallbackTargetId ?? ''}
                onValueChange={(v) =>
                  setForm((p) => ({
                    ...p,
                    transferFallbackTargetId: v || null,
                  }))
                }
              >
                <SelectTrigger className="w-full md:w-[420px]">
                  <SelectValue placeholder="Zielperson aus dem Verzeichnis wählen …" />
                </SelectTrigger>
                <SelectContent>
                  {namedTargets.length === 0 ? (
                    <SelectItem value="__empty__" disabled>
                      Lege zuerst eine Zielperson im Verzeichnis an.
                    </SelectItem>
                  ) : (
                    namedTargets.map((t) => (
                      <SelectItem key={t.id} value={t.id}>
                        {t.displayName}
                        {t.role ? ` — ${t.role}` : ''}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-muted-foreground">
                Wenn der Anrufer sagt „verbinden Sie mich mit einem Mitarbeiter", wird auf
                diese Person weitergeleitet — auch ohne Namensnennung.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ─── Directory ────────────────────────────────────────────── */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <UserRound className="h-5 w-5" /> Verzeichnis — Zielpersonen
          </CardTitle>
          <CardDescription>
            Personen, auf die die KI weiterleiten darf. Bei vollständigem Vor- UND
            Nachnamen verlangt die KI einen vollen Namens-Match. Bei Einträgen mit nur
            einem Namen (z. B. „Fabian") genügt dieser eine Name — vorausgesetzt er ist
            eindeutig. Die Telefonnummer wird der KI nie laut vorgelesen, sondern nur
            zum Wählen verwendet.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-3">
          {targets.length === 0 ? (
            <p className="rounded-md border border-dashed border-border p-4 text-center text-sm text-muted-foreground">
              Noch keine Zielperson hinterlegt.
            </p>
          ) : (
            <div className="flex flex-col gap-3">
              {targets.map((t) => (
                <div
                  key={t.id}
                  className="grid gap-2 rounded-md border border-border p-3 md:grid-cols-[1.4fr_1fr_1fr_auto] md:items-end"
                >
                  <div className="flex flex-col gap-1">
                    <Label className="text-xs">Name (voller Name oder Einzelname)</Label>
                    <Input
                      placeholder="Maria Müller oder Fabian"
                      value={t.displayName ?? ''}
                      onChange={(e) =>
                        updateTarget(t.id, { displayName: e.target.value })
                      }
                    />
                  </div>
                  <div className="flex flex-col gap-1">
                    <Label className="text-xs">Funktion (optional)</Label>
                    <Input
                      placeholder="Technik"
                      value={t.role ?? ''}
                      onChange={(e) => updateTarget(t.id, { role: e.target.value })}
                    />
                  </div>
                  <div className="flex flex-col gap-1">
                    <Label className="text-xs">Telefonnummer (E.164)</Label>
                    <Input
                      placeholder="+49301234567"
                      value={t.phoneNumber ?? ''}
                      onChange={(e) =>
                        updateTarget(t.id, { phoneNumber: e.target.value })
                      }
                    />
                  </div>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => deleteTarget(t.id)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}
          <Button variant="outline" size="sm" onClick={addTarget} className="self-start">
            <Plus className="mr-2 h-4 w-4" /> Zielperson hinzufügen
          </Button>
          {!directoryEligible && targets.length > 0 && (
            <p className="text-[11px] text-amber-500">
              Hinweis: Im aktuellen Modus „
              {MODE_OPTIONS.find((o) => o.value === mode)?.label}" wird die KI keine
              namensbasierten Weiterleitungen durchführen, auch wenn das Verzeichnis
              gepflegt ist.
            </p>
          )}
        </CardContent>
      </Card>

      {/* ─── Trusted callers ───────────────────────────────────────── */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5" /> Vertrauenswürdige Anrufer
          </CardTitle>
          <CardDescription>
            Nummern, denen du vertraust (Geschäftspartner, Außendienst, Stammkunden).
            Wenn jemand von einer dieser Nummern anruft, lockert sich die strenge
            Vor-+Nachname-Pflicht — Vorname ODER Nachname genügt, sofern eindeutig. Die
            Aktivierung passiert automatisch anhand der CallerID.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-3">
          {trusted.length === 0 ? (
            <p className="rounded-md border border-dashed border-border p-4 text-center text-sm text-muted-foreground">
              Noch keine vertrauenswürdige Nummer hinterlegt.
            </p>
          ) : (
            <div className="flex flex-col gap-3">
              {trusted.map((t) => (
                <div
                  key={t.id}
                  className="grid gap-2 rounded-md border border-border p-3 md:grid-cols-[1fr_1.4fr_auto] md:items-end"
                >
                  <div className="flex flex-col gap-1">
                    <Label className="text-xs">Telefonnummer (E.164)</Label>
                    <Input
                      placeholder="+49301234567"
                      value={t.phoneNumber ?? ''}
                      onChange={(e) =>
                        updateTrusted(t.id, { phoneNumber: e.target.value })
                      }
                    />
                  </div>
                  <div className="flex flex-col gap-1">
                    <Label className="text-xs">Bezeichnung (optional)</Label>
                    <Input
                      placeholder="Außendienst Süd, Herr Bauer"
                      value={t.displayName ?? ''}
                      onChange={(e) =>
                        updateTrusted(t.id, { displayName: e.target.value })
                      }
                    />
                  </div>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => deleteTrusted(t.id)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}
          <Button variant="outline" size="sm" onClick={addTrusted} className="self-start">
            <Plus className="mr-2 h-4 w-4" /> Vertrauenswürdige Nummer hinzufügen
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}

function cryptoRandomId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return (crypto as Crypto).randomUUID();
  }
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
}
